import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
    type CoworkerPreferences,
    type CoworkerStore,
    type MsalProfile,
    type StorePreferencesConfig,
    getCoworkerProfile,
} from '@repo/utils';

interface CoworkerProfileState {
    msalProfile: MsalProfile | null;
    coworkerPreferences: CoworkerPreferences | null;
    store: CoworkerStore | null;
    storePreferencesConfig: StorePreferencesConfig | null;
}

interface CoworkerProfileActions {
    setCoworkerPreferences: () => void;
    setCoworkerPreferencesValue: (coworkerPreferences: CoworkerPreferences) => void;
    setMsalProfile: () => void;
    setStore: () => void;
    setStorePreferencesConfig: () => void;
    setCoworkerProfile: () => void;
}

const storeName = 'coworker-profile-store';
const registeredListeners = new Set<keyof CoworkerProfileState>();

const createDataHandler =
    (
        set: (
            state: Partial<CoworkerProfileState> | ((state: CoworkerProfileState) => Partial<CoworkerProfileState>)
        ) => void,
        dataKey: keyof CoworkerProfileState
    ) =>
    () => {
        try {
            const data = getCoworkerProfile()?.data?.[dataKey] ?? null;
            set((state: CoworkerProfileState) => ({
                [dataKey]: state[dataKey] ? { ...state[dataKey], ...data } : data,
            }));

            // Only add listener if not already registered
            if (!registeredListeners.has(dataKey)) {
                const handler = () => {
                    const data = getCoworkerProfile()?.data?.[dataKey] ?? null;
                    set((state: CoworkerProfileState) => ({
                        [dataKey]: state[dataKey] ? { ...state[dataKey], ...data } : data,
                    }));
                };

                globalThis.addEventListener(dataKey, handler);
                registeredListeners.add(dataKey);
            }
        } catch (error) {
            // oxlint-disable-next-line no-console
            console.error(error);
        }
    };

export const useCoworkerProfileStore = create<CoworkerProfileState & CoworkerProfileActions>()(
    devtools(
        (set, get) => ({
            coworkerPreferences: null,
            msalProfile: null,
            store: null,
            storePreferencesConfig: null,
            setCoworkerProfile: () => {
                const { setCoworkerPreferences, setMsalProfile, setStore, setStorePreferencesConfig } = get();
                setCoworkerPreferences();
                setMsalProfile();
                setStore();
                setStorePreferencesConfig();
            },
            setCoworkerPreferences: createDataHandler(set, 'coworkerPreferences'),
            setCoworkerPreferencesValue: (coworkerPreferences) => {
                set((state: CoworkerProfileState) => ({
                    coworkerPreferences: state.coworkerPreferences
                        ? { ...state.coworkerPreferences, ...coworkerPreferences }
                        : coworkerPreferences,
                }));
            },
            setMsalProfile: createDataHandler(set, 'msalProfile'),
            setStore: createDataHandler(set, 'store'),
            setStorePreferencesConfig: createDataHandler(set, 'storePreferencesConfig'),
        }),
        {
            name: storeName,
        }
    )
);
